import {useTranslation} from "react-i18next";
import React, {useCallback, useEffect, useState} from "react";
import {formatDateTime, getMeterTypeIcon} from "../../../utils/utils";
import Chart from "react-apexcharts";

import DE from "apexcharts/dist/locales/de.json"
import EN from "apexcharts/dist/locales/en.json"
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";


const TypeToChartOpt = {
    'DR': {title: "DR", minY: 0, maxY: undefined},
    'SNR': {title: "SNR", minY: 0, maxY: undefined},
    'RSSI': {title: "RSSI", minY: undefined, maxY: 0},
}

const getChartLineOptions = (i18n, type, data, time) => {
    const opt = TypeToChartOpt[type]

    return {
        series: [
            {
                name: opt.title,
                data: data,
            },
        ],
        options: {
            chart: {
                locales: [DE, EN],
                defaultLocale: i18n.resolvedLanguage,
                height: 350,
                type: "line",
                toolbar: {
                    tools: {
                        download: false,
                        selection: false,
                        pan: false,
                    }
                },
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            },
            title: {
                text: opt.title,
                align: 'left'
            },
            grid: {
                row: {
                    colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                    opacity: 0.5
                },
            },
            xaxis: {
                categories: time,
                labels: {
                    show: false,
                }
            },
            yaxis: {
                min: opt.minY,
                max: opt.maxY
            },
        },
    }
}

const getChartHeatmapOptions = (i18n, type, data, time) => {
    const opt = TypeToChartOpt[type]

    function generateData(count, yrange) {
        var i = 0;
        var series = [];
        while (i < count) {
            var x = 'w' + (i + 1).toString();
            var y = Math.floor(Math.random() * (yrange.max - yrange.min + 1)) + yrange.min;

            series.push({
                x: x,
                y: y
            });
            i++;
        }
        return series;
    }

    return {
        series: data.sort((a, b) => a.name - b.name),
        options: {
            chart: {
                height: 350,
                type: 'heatmap',
            },
            dataLabels: {
                enabled: false
            },
            xaxis: {
                categories: time,
            },
            title: {
                text: opt.title
            },
        },
    }
}

export const MetricsCharts = ({smartReader, metrics}) => {
    const {t, i18n} = useTranslation()
    const [chartDRData, setChartDRData] = useState(getChartHeatmapOptions(i18n, "DR", [], []));
    const [chartRSSIData, setChartRSSIData] = useState(getChartLineOptions(i18n, "RSSI", [], []));
    const [chartSNRData, setChartSNRData] = useState(getChartLineOptions(i18n, "SNR", [], []));

    function processDRData(data) {
        const uniqueDRs = new Set();
        data.forEach(item => {
            uniqueDRs.add(...Object.keys(item.dr));
        });
        uniqueDRs.delete(undefined)

        const dataSeries = [...uniqueDRs].map(dr => ({
            name: dr,
            data: []
        }));


        data.forEach(item => {
            dataSeries.forEach(series => {
                if (series.name in item.dr) {
                    series.data.push({
                        x: item.bucket,
                        y: item.dr[series.name]
                    });
                } else {
                    series.data.push({
                        x: item.bucket,
                        y: 0
                    });
                }
            });
        });

        return dataSeries;
    }

    useEffect(() => {
        // for apexcharts - https://apexcharts.com/react-chart-demos/line-charts/
        const xAxisDates = metrics.map((metric) => formatDateTime(metric.bucket))

        setChartDRData(getChartHeatmapOptions(i18n, t("DR"), processDRData(metrics), xAxisDates))
        setChartRSSIData(getChartLineOptions(i18n, t("RSSI"), metrics.map((metric) => metric.rssi__avg), xAxisDates))
        setChartSNRData(getChartLineOptions(i18n, t("SNR"), metrics.map((metric) => metric.snr__avg), xAxisDates))
    }, [metrics]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <Typography sx={{
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    marginTop: 1.2,
                    textAlign: "center"
                }}>
                    Data Rate Metrics
                </Typography>
                <Chart
                    options={chartDRData.options}
                    series={chartDRData.series}
                    type="heatmap"
                />
            </Grid>

            <Grid item xs={6}>
                <Typography sx={{
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    marginTop: 1.2,
                    textAlign: "center"
                }}>
                    Signal/Noise Ratio
                </Typography>
                <Chart
                    options={chartSNRData.options}
                    series={chartSNRData.series}
                    type="line"
                />
            </Grid>

            <Grid item xs={6}>
                <Typography sx={{
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    marginTop: 1.2,
                    textAlign: "center"
                }}>
                    Received Signal Strength Indication
                </Typography>
                <Chart
                    options={chartRSSIData.options}
                    series={chartRSSIData.series}
                    type="line"
                />
            </Grid>
        </Grid>
    );
};